import React from "react"
import { Card, Space, Typography, Tag, Divider, Button, message } from "antd"
import { 
  CheckCircleOutlined, 
  ClockCircleOutlined, 
  CloseCircleOutlined, 
  CopyOutlined, 
  DownloadOutlined, 
  QrcodeOutlined, 
  MailOutlined, 
  ReloadOutlined 
} from "@ant-design/icons"
import { formatDateTime } from "../../utils"
import { IMovementRS } from "../../models/storageModel"

const { Text } = Typography

export interface MovementDetailElectronicInvoiceCardProps {
  movement: Partial<IMovementRS>
  retrying: boolean
  onRetryEmission: () => void
  onOpenEmailModal: () => void
}

const MovementDetailElectronicInvoiceCard: React.FC<MovementDetailElectronicInvoiceCardProps> = ({
  movement,
  retrying,
  onRetryEmission,
  onOpenEmailModal,
}) => {
  if (movement.invoice_type !== 'ELECTRONIC_INVOICE') return null

  return (
    <Card 
      title={
        <Space>
          <Tag color="green">DIAN</Tag>
          <span>Factura Electrónica</span>
        </Space>
      } 
      className="card-shadow" 
      style={{ marginBottom: 16, borderTop: '3px solid #52c41a' }}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <div>
          <Text type="secondary">Estado DIAN:</Text>
          <div style={{ marginTop: 4 }}>
            {movement.electronic_invoice_status === 'ACCEPTED' ? (
              <Tag icon={<CheckCircleOutlined />} color="success" style={{ fontSize: 13, padding: '2px 8px' }}>
                Validada y Aceptada
              </Tag>
            ) : movement.electronic_invoice_status === 'PENDING' ? (
              <Tag icon={<ClockCircleOutlined />} color="warning" style={{ fontSize: 13, padding: '2px 8px' }}>
                Pendiente de Validación
              </Tag>
            ) : movement.electronic_invoice_status === 'REJECTED' ? (
              <Tag icon={<CloseCircleOutlined />} color="error" style={{ fontSize: 13, padding: '2px 8px' }}>
                Rechazada por DIAN
              </Tag>
            ) : (
              <Tag color="default">No emitida</Tag>
            )}
          </div>
        </div>

        {movement.electronic_invoice_number && (
          <div>
            <Text type="secondary">Número Oficial FE:</Text>
            <div style={{ fontWeight: 600, fontSize: 15, color: '#1890ff' }}>
              {movement.electronic_invoice_number}
            </div>
          </div>
        )}

        {movement.electronic_invoice_cufe && (
          <div>
            <Text type="secondary">CUFE:</Text>
            <div style={{ 
              fontSize: 11, 
              backgroundColor: '#f5f5f5', 
              padding: 6, 
              borderRadius: 4, 
              wordBreak: 'break-all',
              fontFamily: 'monospace',
              marginTop: 4
            }}>
              {movement.electronic_invoice_cufe}
            </div>
            <Button 
              type="link" 
              size="small" 
              icon={<CopyOutlined />} 
              style={{ padding: 0, marginTop: 4 }}
              onClick={() => {
                navigator.clipboard.writeText(movement.electronic_invoice_cufe || "")
                message.success("CUFE copiado al portapapeles")
              }}
            >
              Copiar CUFE
            </Button>
          </div>
        )}

        {movement.electronic_invoice_validated_at && (
          <div>
            <Text type="secondary">Validada el:</Text>
            <div style={{ fontSize: 12 }}>
              {formatDateTime(movement.electronic_invoice_validated_at)}
            </div>
          </div>
        )}

        <Divider style={{ margin: '8px 0' }} />

        <Space direction="vertical" style={{ width: '100%' }}>
          {movement.electronic_invoice_public_url && (
            <Button 
              type="primary" 
              icon={<DownloadOutlined />} 
              style={{ width: '100%', backgroundColor: '#52c41a', borderColor: '#52c41a' }}
              onClick={() => window.open(movement.electronic_invoice_public_url!, '_blank')}
            >
              Descargar Factura Electrónica (PDF)
            </Button>
          )}

          {movement.electronic_invoice_qr_url && (
            <Button 
              icon={<QrcodeOutlined />} 
              style={{ width: '100%' }}
              onClick={() => window.open(movement.electronic_invoice_qr_url!, '_blank')}
            >
              Verificar en Catálogo DIAN (QR)
            </Button>
          )}

          {movement.electronic_invoice_number && (
            <Button 
              icon={<MailOutlined />} 
              style={{ width: '100%' }}
              onClick={onOpenEmailModal}
            >
              Reenviar por Correo
            </Button>
          )}

          {movement.electronic_invoice_status !== 'ACCEPTED' && (
            <Button 
              icon={<ReloadOutlined />} 
              type="dashed" 
              danger 
              loading={retrying}
              style={{ width: '100%' }}
              onClick={onRetryEmission}
            >
              Reintentar Emisión ante DIAN
            </Button>
          )}
        </Space>
      </Space>
    </Card>
  )
}

export default MovementDetailElectronicInvoiceCard
